/* eslint-disable react/forbid-prop-types */
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isNull from 'lodash/isNull';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox, Typography, AppBar, Tabs, Tab, FormControlLabel, Switch,
} from '@material-ui/core';
import { InlineTimePicker } from 'material-ui-pickers';
import { sevenDaysInAWeek } from '../../data/Constants';
import { hourStyle, resourceFormStyle } from '../../css/style';
import * as HoursUtils from '../../utils/HoursUtils';
import { updateHoursToNewDatabaseFormat } from '../../utils/Functions';
import moment from 'moment';
import DeleteIcon from '@material-ui/icons/Delete';
import withStyles from '@material-ui/core/styles/withStyles';
import { AccessTime } from '@material-ui/icons';

const styles = hourStyle();

const TABS = HoursUtils.HOUR_TYPES;

const TABS_ARRAY = HoursUtils.HOUR_TYPES_ARRAY;

const MAX_TIME = '23:59';

const SEVEN_DAYS_OF_WEEK = Object.keys(sevenDaysInAWeek);

const switchStyles = resourceFormStyle();
const IosSwitch = withStyles(switchStyles.iosSwitchStyles)(Switch);

class Hours extends Component {
  static propTypes = {
    data: PropTypes.arrayOf(PropTypes.object),
    onChange: PropTypes.func.isRequired,
  }

  static defaultProps = {
    data: null,
  }

  constructor(props) {
    super(props);
    const { data, onChange } = props;
    let hours = [];
    let newHourTempIdCount = -1;
    SEVEN_DAYS_OF_WEEK.map((dayName) => {
      const existingHoursForDay = data ? data.filter(dayHours => dayHours.day === dayName) : [];
      const { tempRecords, newTempIdCounter } = HoursUtils.initializeDayWithTempRecords(dayName, newHourTempIdCount);
      newHourTempIdCount = newTempIdCounter;
      hours = existingHoursForDay.length > 0 ? [...hours, ...existingHoursForDay] : [...hours, ...tempRecords];
    });

    hours = HoursUtils.initializeHours(hours);
    const overlappingRecordIds = HoursUtils.getAllOverlappingHours(hours, {});

    // If open and close times are null then location is closed

    this.state = {
      hours,
      deliverySameAsOpeningCheckbox: false,
      pickupSameAsOpeningCheckbox: false,
      tabName: TABS.business,
      newHourTempIdCount,
      overlappingRecordIds,
    };

    onChange(hours);
  }

  isSameHourRecord = (hourToUpdate, existingHourState) => {
    const isSameHour = hourToUpdate.id && existingHourState.id === hourToUpdate.id;
    const isSameNewHour = hourToUpdate.tempId && existingHourState.tempId === hourToUpdate.tempId;
    return isSameHour || isSameNewHour;
  }

  onChangeHours = (momentObj, hourToUpdate, openOrClose) => {
    const inputVal = momentObj ? momentObj.format('HH:mm') : null;
    let { hours, tabName, overlappingRecordIds } = this.state;
    const { closed } = this.getHourSettingsLookupKeys();
    const { onChange } = this.props;

    hours = hours.map((hour, index, array) => {
      if (hour.day !== hourToUpdate.day) return hour;
      let hourClone = Object.assign({}, hour);
      const isClosed = hourClone[closed];
      // This unchecks Closed for all matching days if we add an open or close time.
      if (inputVal !== null && isClosed) hourClone[closed] = false;

      if (!this.isSameHourRecord(hourToUpdate, hour)) return hourClone;

      hourClone[openOrClose] = inputVal === null ? null : inputVal;
      return hourClone;
    });

    const checkOverlapsArgs = {
      changedHour: hourToUpdate,
      allHourRecords: hours,
      tab: tabName,
      overlappingRecordIds,
    };
    const newOverlappingRecordIds = HoursUtils.getOverlapsOnChangeHour(checkOverlapsArgs);

    this.setState({ hours, overlappingRecordIds: newOverlappingRecordIds });
    onChange(hours);
  }

  onOpenSwitchChange = (hourToUpdate) => {
    const {
      open: openHourFieldName,
      close: closeHourFieldName,
      closed: closedFieldName,
      openAllDay,
    } = this.getHourSettingsLookupKeys();

    const { onChange } = this.props;
    const { hours, overlappingRecordIds } = this.state;

    const newHours = hours.map((hour) => {
      if (hour.day !== hourToUpdate.day) return hour;
      if (!this.isHourRecordForDeliveryOption(hour)) return hour;

      let hourClone = cloneDeep(hour);
      const currentIsOpen = !hourClone[closedFieldName];

      // Going to checked: true
      if (currentIsOpen) {
        hourClone[openHourFieldName] = null;
        hourClone[closeHourFieldName] = null;
        hourClone[openAllDay] = false;
        hourClone[closedFieldName] = true;
      } else {
        // Going to checked: false
        // Use null to hide empty existing records and pass validations so that e.g. several cleared existing records don't show up
        hourClone[closedFieldName] = this.isSameHourRecord(hourToUpdate, hourClone) ? false : null;
      }

      return hourClone;
    });

    const newOverlappingRecordIds = HoursUtils.getAllOverlappingHours(newHours, overlappingRecordIds);

    this.setState({ hours: newHours, overlappingRecordIds: newOverlappingRecordIds });
    onChange(newHours);
  }

  getTime = (hour) => {
    const timeArray = hour.split(':');
    const time = new Date();
    time.setHours(timeArray[0]);
    time.setMinutes(timeArray[1]);

    return time;
  }

  getHoursContainerStyle = (isHourSettingsValid, id) => {
    const { overlappingRecordIds, tabName } = this.state;
    const noTimeOverlaps = !get(overlappingRecordIds, `${id}.${tabName}`);
    return isHourSettingsValid && noTimeOverlaps ? styles.hoursContainer : styles.hoursContainerWithRedBorder;
  }

  wholeDayIsClosedCheckboxOnFirstHourEntry = (hour, isPrimary) => {
    const hoursSettingsKeys = this.getHourSettingsLookupKeys();
    const { closed: closedUpdateKey } = hoursSettingsKeys;
    const isClosedCheckboxForWholeDay = (
      <IosSwitch
        checked={!hour[closedUpdateKey]}
        onChange={() => this.onOpenSwitchChange(hour)}
        disableRipple
      />
    );
    return isPrimary ? isClosedCheckboxForWholeDay : <div />;
  }

  isOpen24HoursOnFirstHourEntry = (hour, isPrimary, index) => {
    const hoursSettingsKeys = this.getHourSettingsLookupKeys();
    const {
      openAllDay: openAllDayUpdateKey,
      closed: closedUpdateKey,
    } = hoursSettingsKeys;
    if (hour[closedUpdateKey]) {
      return <div />;
    }
    const allDayOpenCheckboxForWholeDay = (
      <Checkbox
        checked={!!hour[openAllDayUpdateKey]}
        onChange={() => this.onChangeAllDayCheckBox(hour.day, openAllDayUpdateKey, !hour[openAllDayUpdateKey])}
        style={styles.checkBoxColor}
      />
    );
    return isPrimary ? allDayOpenCheckboxForWholeDay : <div />;
  }

  getHourSettingsLookupKeys = () => {
    const { tabName } = this.state;
    return HoursUtils.HOUR_SETTINGS[tabName];
  }

  renderOpenCloseTimePickers = (hour, parsedTime, hourSettingsUpdateKey) => {
    const { classes } = this.props;
    const hourSettingsLookupKeys = this.getHourSettingsLookupKeys();
    if (hour[hourSettingsLookupKeys.openAllDay] || hour[hourSettingsLookupKeys.closed]) {
      return <div style={styles.timePickerLikeDiv} />;
    }
    return (
      <InlineTimePicker
        clearable
        disableOpenOnEnter
        keyboard
        value={parsedTime}
        onChange={momentObj => this.onChangeHours(momentObj, hour, hourSettingsUpdateKey)}
        mask={[/\d/, /\d/, ':', /\d/, /\d/, ' ', /a|p/i, 'M']}
        style={styles.timePickerWidth}
        invalidDateMessage='Invalid time'
        FormHelperTextProps={{
          classes: {
            root: classes.helperText,
          },
        }}
        keyboardIcon={<AccessTime />}
      />
    );
  }

  clearValuesOfTabFromRecord = (targetHour) => {
    const { hours, tabName } = this.state;
    const {
      closed,
      open,
      close,
      openAllDay,
    } = this.getHourSettingsLookupKeys();

    const newValues = {
      [closed]: null,
      [open]: null,
      [close]: null,
      [openAllDay]: false,
    };

    const updatedHours = hours.map((hourRecord) => {
      if (!this.isSameHourRecord(hourRecord, targetHour)) return hourRecord;
      return Object.assign({}, hourRecord, newValues);
    });
    return updatedHours;
  }

  deleteHour = targetHour => () => {
    const { hours } = this.state;
    const { onChange } = this.props;
    const isTempRecord = targetHour.tempId;

    const newHours = isTempRecord
      ? hours.filter(hour => !this.isSameHourRecord(hour, targetHour))
      : this.clearValuesOfTabFromRecord(targetHour);

    this.setState({ hours: newHours, prevHoursForUndo: newHours });
    onChange(newHours);
  }

  renderDeleteHourButton = hour => (
    <div style={styles.checkboxContainer}>
      <DeleteIcon
        onClick={this.deleteHour(hour)}
        style={styles.trashCanIcon}
      />
    </div>
  )

  render24HoursLabelAndCheckbox = (hour, isPrimary) => {
    const allDayOpenCheckboxOnFirstHourEntry = this.isOpen24HoursOnFirstHourEntry(hour, isPrimary);
    const { closed: closedUpdateKey } = this.getHourSettingsLookupKeys();
    return !hour[closedUpdateKey] ? (
      <FormControlLabel
        control={allDayOpenCheckboxOnFirstHourEntry}
        label={isPrimary ? '24 Hours' : ''}
        style={styles.checkboxContainer}
      />
    ) : <div style={styles.checkboxContainer} />;
  }

  shouldShowAddHour = (hour, index, isPrimary) => {
    if (!isPrimary) return false;
    const { closed, openAllDay } = this.getHourSettingsLookupKeys();
    const isClosed = hour[closed];
    const isOpenAllDay = hour[openAllDay];
    return !isClosed && !isOpenAllDay;
  }

  showAddHour = (hour, index, isPrimary) => {
    const shouldShow = this.shouldShowAddHour(hour, index, isPrimary);
    return (
      <Typography
        style={styles.addHourLink}
        onClick={() => this.onAddHour(hour)}
      >
        {shouldShow ? 'add hour' : ''}
      </Typography>
    );
  }


  renderHoursOfDays = (day, hour, index, tabName, isPrimary) => {
    const hourSettingsLookupKeys = this.getHourSettingsLookupKeys();
    const {
      closed,
      open,
      close,
      openAllDay,
    } = hourSettingsLookupKeys;

    const hourId = HoursUtils.getIdOfRecord(hour);

    const openTimeToRender = get(hour, open, '');
    const closeTimeToRender = get(hour, close, '');

    /**
     * The Material UI Pickers only ever set valid values.
     * They emit onChange events only when you finish typing/selecting a value.
     * Invalid starting values are fixed by Functions.hoursSafariFix
     */
    const isValid = HoursUtils.isSettingsInHourValid(hour, hourSettingsLookupKeys);

    // Material UI date(time) Pickers use the date management library set in the top-level Provider to parse values
    const parsedOpenTimeForTimePicker = openTimeToRender ? moment(openTimeToRender, 'HH:mm') : null;
    const parsedCloseTimeForTimePicker = closeTimeToRender ? moment(closeTimeToRender, 'HH:mm') : null;

    const isClosedCheckboxOnFirstHourEntry = this.wholeDayIsClosedCheckboxOnFirstHourEntry(hour, isPrimary);

    const openAllDayCheckbox = this.render24HoursLabelAndCheckbox(hour, isPrimary);
    let label;
    if (isPrimary && hour[closed]) {
      label = 'Closed';
    } else if (isPrimary && !hour[closed]) {
      label = 'Open';
    } else {
      label = '';
    }

    const dashInBetween = (!hour[closed] && !hour[openAllDay])
      ? (<div style={styles.dashInbetween}> - </div>)
      : (<div style={styles.dashInbetween} />);

    const dayInWeekTextStyle = !hour[closed] ? styles.dayInWeekText : styles.dayInWeekTextDisabled;

    return (
      <div
        key={hour.id}
        style={this.getHoursContainerStyle(isValid, hourId)}
      >
        <div style={dayInWeekTextStyle}>
          {isPrimary ? `${day} :` : ''}
        </div>
        <div style={styles.timeInputContainer}>
          <FormControlLabel
            control={isClosedCheckboxOnFirstHourEntry}
            label={label}
            style={styles.checkboxContainer}
          />
          {this.renderOpenCloseTimePickers(hour, parsedOpenTimeForTimePicker, open)}
          {dashInBetween}
          {this.renderOpenCloseTimePickers(hour, parsedCloseTimeForTimePicker, close)}
          {isPrimary && openAllDayCheckbox}
          {!isPrimary && this.renderDeleteHourButton(hour)}
        </div>
        {this.showAddHour(hour, index, isPrimary)}
      </div>
    );
  }

  onAddHour = (existingDayHour) => {
    const { hours, newHourTempIdCount, tabName } = this.state;
    const {
      open,
      close,
      closed,
      openAllDay,
    } = this.getHourSettingsLookupKeys();
    const { onChange } = this.props;
    const defaultHour = {
      tempId: newHourTempIdCount,
      day: existingDayHour.day,
      [open]: null,
      [close]: null,
      [closed]: false,
      [openAllDay]: existingDayHour[openAllDay],
    };
    const newHours = cloneDeep(hours);
    const sameDayRecords = newHours.filter(hour => hour.day == existingDayHour.day);
    const indexOfFirstRecordWithSpace = sameDayRecords.findIndex(record => HoursUtils.isRecordOverwriteable(record, tabName));
    if (indexOfFirstRecordWithSpace !== -1) {
      const firstRecordWithSpace = sameDayRecords[indexOfFirstRecordWithSpace];
      firstRecordWithSpace[closed] = false;
      firstRecordWithSpace[openAllDay] = existingDayHour[openAllDay];
    } else {
      newHours.splice(newHours.find(hour => hour.day == existingDayHour.day), 0, defaultHour);
    }

    this.setState({ hours: newHours, newHourTempIdCount: newHourTempIdCount - 1 });
    onChange(newHours);
  }

  onChangeAllDayCheckBox = (day, key, value) => {
    const { onChange } = this.props;
    const { hours, overlappingRecordIds } = this.state;
    const { closed, open, close } = this.getHourSettingsLookupKeys();
    let prevDay;

    const updatedHours = hours.map((hour) => {
      if (hour.day !== day) return hour;
      if (!this.isHourRecordForDeliveryOption(hour)) return hour;

      let hourClone = Object.assign({}, hour);
      hourClone[key] = value;

      // Going to checked: true
      if (value === true) {
        hourClone[closed] = null;
        hourClone[open] = null;
        hourClone[close] = null;
      }

      if (value === false && prevDay !== day) {
        hourClone[closed] = false;
        prevDay = day;
      }

      return hourClone;
    });

    const newOverlappingRecordIds = HoursUtils.getAllOverlappingHours(updatedHours, overlappingRecordIds);

    this.setState({ hours: updatedHours, overlappingRecordIds: newOverlappingRecordIds });
    onChange(updatedHours);
  }


  onSameAsOpeningCheckBoxChange = () => {
    const { onChange } = this.props;
    const hourSettingsLookupKeys = this.getHourSettingsLookupKeys();

    const { hours, tabName, overlappingRecordIds } = this.state;
    const checkboxStateKey = `${tabName}SameAsOpeningCheckbox`;
    const undoHoursStateKey = `${tabName}PrevHoursForUndo`;
    const { [undoHoursStateKey]: prevHoursForUndo, [checkboxStateKey]: checkboxValue } = this.state;

    let newHours = cloneDeep(hours);

    // If you are going from checked: false to checked: true Same As Opening, you want the hours updated, but not if you're going the other way
    const hoursToSaveForUndoSameAsOpening = checkboxValue ? null : hours;

    const nonTargetDeliveryOption = tabName === HoursUtils.HOUR_TYPES.delivery
      ? HoursUtils.HOUR_TYPES.pickup
      : HoursUtils.HOUR_TYPES.delivery;
    // Going from checked: false to checked: true
    if (!checkboxValue) newHours = HoursUtils.setBusinessHoursOnDeliveryOption(newHours, tabName, nonTargetDeliveryOption);

    // Going from checked: true to checked: false
    if (checkboxValue && prevHoursForUndo) {
      newHours = HoursUtils.setPrevDeliveryHours(prevHoursForUndo, newHours);
    }

    const newOverlappingRecordIds = HoursUtils.getAllOverlappingHours(newHours, overlappingRecordIds);

    onChange(newHours);
    this.setState({
      overlappingRecordIds: newOverlappingRecordIds,
      hours: newHours,
      [checkboxStateKey]: !checkboxValue,
      [undoHoursStateKey]: hoursToSaveForUndoSameAsOpening,
    });
  }

  /**
   * Depends on temp records added by Hours.onAddHour taking a certain value
   * The temp records use null instead of undefined for empty opening and closing times,
   * using null will make them count as present in the delivery option
   */
  isHourRecordForDeliveryOption = (hourRecord) => {
    const hourSettingsKeys = this.getHourSettingsLookupKeys();

    return HoursUtils.isHourRecordForDeliveryOption(hourRecord, hourSettingsKeys);
  }

  filterEmptyRecordsForRenderingTab = (record, ind) => {
    const { tabName } = this.state;
    if (ind === 0) return true;
    return !HoursUtils.isHourTypeEmpty(record, tabName);
  }

  hideRecordsClearedByClosedOrOpenAllDay = (record, ind) => {
    const hourSettingsKeys = this.getHourSettingsLookupKeys();
    const { closed, openAllDay } = hourSettingsKeys;
    const { open, close } = hourSettingsKeys;
    if (ind === 0) return true;
    if (record[closed]) {
      const isClearedByCloseCheckbox = isNull(record[open]) && isNull(record[close]) && !record[openAllDay];
      return !isClearedByCloseCheckbox;
    } else if (record[openAllDay]) {
      const isClearedByOpenAllDayCheckbox = isNull(record[closed]) && isNull(record[open]) && isNull(record[close]);
      return !isClearedByOpenAllDayCheckbox;
    }
    return true;
  }

  renderWeekHours = () => {
    const { tabName, hours } = this.state;

    if (!Array.isArray(hours)) return null;
    const weekHours = SEVEN_DAYS_OF_WEEK.map((day, index) => {
      const dayHours = hours.filter(hour => hour.day === day);
      const { open } = this.getHourSettingsLookupKeys();

      const recordsForDeliveryOption = dayHours.filter(this.isHourRecordForDeliveryOption);
      const sortedDeliveryOptionHours = recordsForDeliveryOption.sort((a, b) => moment(a[open] || MAX_TIME, 'HH:mm') - moment(b[open] || MAX_TIME, 'HH:mm'));
      const filterOutEmptyRecords = sortedDeliveryOptionHours.filter(this.filterEmptyRecordsForRenderingTab);
      const withoutClearedRecords = filterOutEmptyRecords.filter(this.hideRecordsClearedByClosedOrOpenAllDay);
      return withoutClearedRecords.map((hour, ind) => this.renderHoursOfDays(sevenDaysInAWeek[day], hour, index, tabName, ind === 0));
    });
    return weekHours;
  }

  getTabClassesObj = (hasError) => {
    const { classes } = this.props;
    const isValidClass = hasError ? classes.tabWithError : classes.tabHeader;
    return {
      labelContainer: isValidClass,
    };
  }

  renderTabs = () => {
    const { hoursTabsWithErrors } = this.props;

    return TABS_ARRAY.map((tabName) => {
      if (tabName === HoursUtils.HOUR_TYPES.pickup) return null;
      const { label } = HoursUtils.HOUR_SETTINGS[tabName];
      const hasError = hoursTabsWithErrors && hoursTabsWithErrors[tabName];

      return (
        <Tab
          label={label}
          value={tabName}
          style={styles.tabHeader}
          classes={this.getTabClassesObj(hasError)}
          onClick={() => this.setState({ tabName })}
        />
      );
    });
  }

  render() {
    const { tabName } = this.state;
    return (
      <div>
        <AppBar position="static" color="default" style={styles.tabAppBar}>
          <Tabs
            variant="fullWidth"
            value={this.state.tabName}
            style={{ backgroundColor: '#fff' }}
            indicatorColor="primary"
          >
            {this.renderTabs()}
          </Tabs>
          <div style={styles.tabContentContainer}>
            {
              tabName !== TABS.business
                ? (
                  <div style={styles.sameAsOpeningHoursContainer}>
                    <Checkbox
                      checked={this.state[`${tabName}SameAsOpeningCheckbox`]}
                      onChange={this.onSameAsOpeningCheckBoxChange}
                      style={styles.checkBoxColor}
                    />
                    <Typography style={styles.sameAsOpeningHoursText}>Same as Opening Hours</Typography>
                  </div>
                )
                : <div style={styles.emptyDiv} />
            }
            {this.renderWeekHours()}
          </div>
        </AppBar>
      </div>
    );
  }
}

Hours.propTypes = {
  classes: PropTypes.any,
};

Hours.defaultProps = {
  classes: null,
};

export default withStyles(hourStyle)(Hours);
